import React, { useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Menu, X, User, LogOut, Heart, Music, Shield, Settings } from 'lucide-react';

const Layout = () => {
  const { user, isAuthenticated, isAdmin, isBand, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b-2 border-border sticky top-0 z-50 bg-background/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center gap-2 no-underline hover:no-underline"
              data-testid="logo-link"
            >
              <span className="font-accent text-2xl text-primary tracking-wider">FIND GOOD BANDS</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <Link 
                to="/browse" 
                className="text-foreground hover:text-primary font-mono text-sm uppercase tracking-widest no-underline"
                data-testid="browse-link"
              >
                Browse
              </Link>
              <Link 
                to="/apply" 
                className="text-foreground hover:text-primary font-mono text-sm uppercase tracking-widest no-underline"
                data-testid="apply-link"
              >
                Apply
              </Link>
              
              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="border-2 border-primary text-primary hover:bg-primary hover:text-black font-mono uppercase tracking-widest"
                      data-testid="user-menu-trigger"
                    >
                      <User className="w-4 h-4 mr-2" />
                      {user?.name?.split(' ')[0] || 'Account'}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-48 bg-card border-2 border-border">
                    <DropdownMenuItem 
                      onClick={() => navigate('/profile')}
                      className="font-mono text-sm cursor-pointer"
                      data-testid="profile-menu-item"
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      My Favorites & Playlists
                    </DropdownMenuItem>
                    
                    {(isBand || isAdmin) && (
                      <DropdownMenuItem 
                        onClick={() => navigate('/band-dashboard')}
                        className="font-mono text-sm cursor-pointer"
                        data-testid="band-dashboard-menu-item"
                      >
                        <Music className="w-4 h-4 mr-2" />
                        Band Dashboard
                      </DropdownMenuItem>
                    )}
                    
                    {isAdmin && (
                      <DropdownMenuItem 
                        onClick={() => navigate('/admin')}
                        className="font-mono text-sm cursor-pointer"
                        data-testid="admin-menu-item"
                      >
                        <Shield className="w-4 h-4 mr-2" />
                        Admin Panel
                      </DropdownMenuItem>
                    )}
                    
                    <DropdownMenuSeparator />
                    
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="font-mono text-sm cursor-pointer text-destructive"
                      data-testid="logout-menu-item"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="flex items-center gap-3">
                  <Link to="/login">
                    <Button 
                      variant="ghost" 
                      className="font-mono text-sm uppercase tracking-widest text-foreground hover:text-primary"
                      data-testid="login-button"
                    >
                      Login
                    </Button>
                  </Link>
                  <Link to="/signup">
                    <Button 
                      className="border-2 border-primary bg-primary text-black hover:bg-transparent hover:text-primary font-mono uppercase tracking-widest btn-brutal"
                      data-testid="signup-button"
                    >
                      Sign Up
                    </Button>
                  </Link>
                </div>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-foreground hover:text-primary"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t-2 border-border bg-background">
            <div className="px-4 py-4 space-y-3">
              <Link 
                to="/browse" 
                className="block text-foreground hover:text-primary font-mono text-sm uppercase tracking-widest no-underline py-2"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-browse-link"
              >
                Browse
              </Link>
              <Link 
                to="/apply" 
                className="block text-foreground hover:text-primary font-mono text-sm uppercase tracking-widest no-underline py-2"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-apply-link"
              >
                Apply
              </Link>
              
              {isAuthenticated ? (
                <>
                  <Link 
                    to="/profile" 
                    className="block text-foreground hover:text-primary font-mono text-sm uppercase tracking-widest no-underline py-2"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-profile-link"
                  >
                    My Profile
                  </Link>
                  {(isBand || isAdmin) && (
                    <Link 
                      to="/band-dashboard" 
                      className="block text-foreground hover:text-primary font-mono text-sm uppercase tracking-widest no-underline py-2"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="mobile-band-dashboard-link"
                    >
                      Band Dashboard
                    </Link>
                  )}
                  {isAdmin && (
                    <Link 
                      to="/admin" 
                      className="block text-foreground hover:text-primary font-mono text-sm uppercase tracking-widest no-underline py-2"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="mobile-admin-link"
                    >
                      Admin Panel
                    </Link>
                  )}
                  <button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="block text-destructive font-mono text-sm uppercase tracking-widest py-2"
                    data-testid="mobile-logout-button"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <div className="flex gap-3 pt-2">
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button 
                      variant="ghost" 
                      className="font-mono text-sm uppercase tracking-widest"
                      data-testid="mobile-login-button"
                    >
                      Login
                    </Button>
                  </Link>
                  <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
                    <Button 
                      className="border-2 border-primary bg-primary text-black hover:bg-transparent hover:text-primary font-mono uppercase tracking-widest"
                      data-testid="mobile-signup-button"
                    >
                      Sign Up
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t-2 border-border mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="font-accent text-xl text-muted-foreground">
              FIND GOOD BANDS
            </div>
            <div className="font-mono text-sm text-muted-foreground">
              © 2026 • ALL RIGHTS RESERVED
            </div>
            <div className="flex gap-4">
              <Link to="/browse" className="text-muted-foreground hover:text-primary font-mono text-xs uppercase no-underline">
                Browse
              </Link>
              <Link to="/apply" className="text-muted-foreground hover:text-primary font-mono text-xs uppercase no-underline">
                Apply
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
